import { HttpErrorResponse } from '@angular/common/http';
import { SecretApiError } from '../interfaces/secrets.interface';

export class SecretErrorHandlingUtils {
  /**
   * Extract user-friendly error message from secret API errors
   */
  static getSecretErrorMessage(error: any): string {
    if (error instanceof HttpErrorResponse) {
      // Handle structured API errors
      if (error.error && typeof error.error === 'object') {
        const apiError = error.error as SecretApiError;
        
        switch (apiError.error) {
          case 'PermissionDenied':
            return this.getPermissionDeniedMessage(apiError.message);
          case 'NotFound':
            return this.getNotFoundMessage(apiError.message);
          case 'UnknownError':
            return this.getVaultErrorMessage(apiError.message);
          default:
            return this.extractErrorMessage(error.error, 'An unexpected error occurred');
        }
      }

      // Handle HTTP status codes
      switch (error.status) {
        case 400:
          return this.extractErrorMessage(error.error, 'Invalid request. Please check your input.');
        case 401:
          return 'Authentication required. Please log in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested secret or source was not found.';
        case 422:
          return 'Invalid data provided. Please check your input.';
        case 500:
          return 'Server error occurred. Please try again later.';
        case 503:
          return 'Service temporarily unavailable. Please try again later.';
        default:
          return this.extractErrorMessage(error.error, `HTTP ${error.status}: ${error.statusText}`);
      }
    }

    // Handle network errors
    if (error.name === 'TimeoutError') {
      return 'Request timed out. Please check your connection and try again.';
    }

    if (error.message && error.message.includes('NetworkError')) {
      return 'Network error. Please check your connection.';
    }

    // Fallback to generic error message
    return error.message || 'An unexpected error occurred';
  }

  private static getPermissionDeniedMessage(message: string): string {
    if (message.includes('gettable')) {
      return 'This secret is not marked as gettable and cannot be retrieved.';
    }
    if (message.includes('Write permission')) {
      return 'You need write permission to modify secrets in this project.';
    }
    if (message.includes('Read permission')) {
      return 'You need read permission to view secrets in this project.';
    }
    return 'You do not have permission to access this resource.';
  }

  private static getNotFoundMessage(message: string): string {
    if (message.includes('Secret with id')) {
      return 'The requested secret was not found or may have been deleted.';
    }
    if (message.includes('Source with id')) {
      return 'The requested secret source was not found or may have been deleted.';
    }
    return 'The requested resource was not found.';
  }

  private static getVaultErrorMessage(message: string): string {
    if (message.includes('failed to get secret from vault')) {
      if (message.includes('403')) {
        return 'Vault access denied. Please check your Vault token permissions.';
      }
      if (message.includes('404')) {
        return 'Secret not found in Vault. Please verify the secret path.';
      }
      if (message.includes('401')) {
        return 'Vault authentication failed. Please check your Vault token.';
      }
      if (message.includes('timeout') || message.includes('connection')) {
        return 'Unable to connect to Vault. Please check the Vault URL and network connectivity.';
      }
      return 'Failed to retrieve secret from Vault. Please check your Vault configuration.';
    }

    if (message.includes('vault')) {
      return 'Vault integration error. Please verify your Vault configuration.';
    }

    return message || 'An unknown error occurred.';
  }

  private static extractErrorMessage(errorPayload: unknown, fallback: string): string {
    if (typeof errorPayload === 'string' && errorPayload.trim()) {
      return errorPayload;
    }

    if (errorPayload && typeof errorPayload === 'object') {
      const payload = errorPayload as Record<string, unknown>;
      if (typeof payload['message'] === 'string' && payload['message'].trim()) {
        return payload['message'];
      }
      if (typeof payload['detail'] === 'string' && payload['detail'].trim()) {
        return payload['detail'];
      }
      if (
        typeof payload['error'] === 'string' &&
        payload['error'].trim() &&
        typeof payload['error_description'] !== 'string'
      ) {
        return payload['error'];
      }
      if (
        typeof payload['error_description'] === 'string' &&
        payload['error_description'].trim()
      ) {
        return payload['error_description'];
      }
    }

    return fallback;
  }

  /**
   * Determine if an error is retryable
   */
  static isRetryableError(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      // Retry on server errors and timeouts
      return error.status >= 500 || error.status === 408 || error.status === 429;
    }

    // Retry on network errors
    return error.name === 'TimeoutError' || 
           (error.message && error.message.includes('NetworkError'));
  }

  /**
   * Get suggested action for the user based on error type
   */
  static getSuggestedAction(error: any): string | null {
    if (error instanceof HttpErrorResponse) {
      const apiError = error.error as SecretApiError;
      
      switch (apiError?.error) {
        case 'PermissionDenied':
          return 'Contact your project administrator to request the necessary permissions.';
        case 'NotFound':
          return 'Please refresh the page or verify the resource still exists.';
        case 'UnknownError':
          if (apiError.message.includes('vault')) {
            return 'Check your Vault configuration and ensure the Vault service is accessible.';
          }
          break;
      }

      switch (error.status) {
        case 401:
          return 'Please log out and log back in to refresh your session.';
        case 503:
          return 'Please wait a moment and try again.';
        case 422:
          return 'Please review your input and correct any validation errors.';
      }
    }

    return null;
  }
}
